import { Info } from '@ui/components/icons';
import { NoSSR } from '@ui/components/NoSSR';
import { TextTooltip } from '@ui/components/TextTooltip';
import { GIT_BOOK_OPEN_POSITION } from '@ui/constants/links';
import { PairMarketCountDown } from '@ui/features/RWA/components/PairMarketCountDown';
import { Icon } from '@ui/lib/park/ui/icon';
import { Link } from '@ui/lib/park/ui/link';
import { Text } from '@ui/lib/park/ui/text';
import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';
import { css } from 'styled-system/css';

import {
  MarketStatus,
  UseMarketStatusReturn,
} from '../../hooks/useMarketStatus';

export const MarketStatusMessage = ({
  status,
  isLong,
}: {
  isLong: boolean | null;
  status: UseMarketStatusReturn;
}) => {
  if (status.isDisabled && status.status === MarketStatus.MARKET_CLOSED) {
    return <PairMarketCountDown />;
  }

  if (status.long.isDisabled && getIsMaxOiMessage(status.long)) {
    if (isLong === null || isLong === true) {
      return <OIReachMessage status={status} />;
    }
  }
  if (status.short.isDisabled && getIsMaxOiMessage(status.short)) {
    if (isLong === null || isLong === false) {
      return <OIReachMessage status={status} />;
    }
  }

  return <PairMarketCountDown />;
};

const OIReachMessage = ({ status }: { status: UseMarketStatusReturn }) => {
  const { t } = useTranslation();

  const labels = useMemo(() => {
    if (status.long.isDisabled && status.short.isDisabled) {
      return {
        msg: t('common.Both long and short orders paused (max OI reached)'),
        tooltip: t('common.Max OI limited', {
          interpolation: {
            escapeValue: false,
          },
        }),
      };
    }
    if (status.long.isDisabled) {
      return {
        msg: t('common.Long orders paused (max OI reached)'),
        tooltip: t('common.Max OI limited', {
          interpolation: {
            escapeValue: false,
          },
        }),
      };
    } else {
      return {
        msg: t('common.Short orders paused (max OI reached)'),
        tooltip: t('common.Max OI limited', {
          interpolation: {
            escapeValue: false,
          },
        }),
      };
    }
  }, [status.long.isDisabled, status.short.isDisabled, t]);

  return (
    <Text
      textStyle="text.xsmall"
      color="greyWhite.lightGrey3"
      w="max-content" // keep tooltip arrow align
    >
      {labels.msg}
      <NoSSR>
        <TextTooltip
          content={
            <>
              <Text textStyle="text.xxsmall">{labels.tooltip}</Text>
              <Link
                textStyle="headings.xxsmall"
                mt={2}
                href={GIT_BOOK_OPEN_POSITION}
                target="_blank"
              >
                {t('common.Learn more')}
              </Link>
            </>
          }
          triggerCss={css.raw({
            textDecoration: 'none',
          })}
          contentCss={css.raw({
            maxWidth: '250px',
          })}
        >
          <Icon boxSize="10px" ml={1}>
            <Info />
          </Icon>
        </TextTooltip>
      </NoSSR>
    </Text>
  );
};

function getIsMaxOiMessage(
  status: UseMarketStatusReturn['long'] | UseMarketStatusReturn['short'],
): boolean {
  return (
    status.reason === MarketStatus.MIN_MAX_SIZE_CONFLICT ||
    status.reason === MarketStatus.MAX_LONG_OI_REACH ||
    status.reason === MarketStatus.MAX_SHORT_OI_REACH
  );
}
